import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import { LIVE_QUERY_OPTIONS } from '@/lib/live-query';
import { broadcast } from '@/lib/sync';
import type { EntryBody, EntryDefaults, EntryListItem } from '@/types/entries';
import type { QuickStart } from '@/types/timer';

const SUGGESTION_STALE_MS = 60_000;

/** GET /api/entries for a range (at most 62 days); live-polled while visible (sprint 4 §5.1). */
export function useRangeEntries(from: LocalDate, to: LocalDate): UseQueryResult<EntryListItem[]> {
  return useQuery({
    queryKey: ['entries', 'range', from, to],
    queryFn: async () => (await apiRequest<{ items: EntryListItem[] }>(`/api/entries?from=${from}&to=${to}`)).items,
    ...LIVE_QUERY_OPTIONS,
  });
}

/** Day view list: GET /api/entries for one date. */
export function useDayEntries(date: LocalDate): UseQueryResult<EntryListItem[]> {
  return useRangeEntries(date, date);
}

/** Dashboard quick-start (sprint 4 §4.4). Under ['entries'] so entry invalidation refreshes it. */
export function useQuickStart(enabled: boolean): UseQueryResult<QuickStart> {
  return useQuery({
    queryKey: ['entries', 'quick-start'],
    queryFn: () => apiRequest<QuickStart>('/api/timer/quick-start'),
    enabled,
    ...LIVE_QUERY_OPTIONS,
  });
}

/** Form defaults for a date; always refetched when the form opens. */
export function useEntryDefaults(date: LocalDate, enabled: boolean): UseQueryResult<EntryDefaults> {
  return useQuery({
    queryKey: ['entries', 'defaults', date],
    queryFn: () => apiRequest<EntryDefaults>(`/api/entries/defaults?date=${date}`),
    enabled,
    staleTime: 0,
  });
}

/** Silent: a failed suggestion fetch just shows no suggestions. */
export function useDescriptionSuggestions(q: string, enabled: boolean): UseQueryResult<string[]> {
  return useQuery({
    queryKey: ['entries', 'description-suggestions', q],
    queryFn: async () =>
      (await apiRequest<{ items: string[] }>(`/api/entries/description-suggestions?q=${encodeURIComponent(q)}`)).items,
    enabled,
    staleTime: SUGGESTION_STALE_MS,
    retry: false,
    meta: { silent: true },
  });
}

/** Refreshes every personal derived read: lists, totals, report projects, quick-start, project hours. */
export function useInvalidateEntryData(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['entries'] }),
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] }),
    ]);
  };
}

/** Create (no id) or full-field edit. Errors are shown inline in the form, never as a toast. */
export function useSaveEntry(): UseMutationResult<{ items: EntryListItem[] }, Error, { id?: string; body: EntryBody }> {
  const invalidate = useInvalidateEntryData();
  return useMutation({
    mutationFn: ({ id, body }) =>
      id
        ? apiRequest<{ items: EntryListItem[] }>(`/api/entries/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : apiRequest<{ items: EntryListItem[] }>('/api/entries', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      broadcast({ type: 'entries-changed' });
      await invalidate();
    },
    meta: { silent: true },
  });
}

/** Errors use the global toast. */
export function useDeleteEntry(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateEntryData();
  return useMutation({
    mutationFn: async (id) => {
      await apiRequestNoContent(`/api/entries/${id}`, { method: 'DELETE' });
    },
    onSuccess: async () => {
      broadcast({ type: 'entries-changed' });
      await invalidate();
    },
  });
}
